import React, { useEffect, useMemo, useState, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import {
  formatAdminDashboardSummary,
  pickAdminDashboardLocaleText,
} from "../../shared/locale/admin-dashboard-copy";
import { formatAdminHashedAccountCueLabel } from "../../shared/formatting/format-admin-account-identity";
import { formatAdminAuthModeLabel } from "../../shared/locale/admin-dashboard-status-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import {
  createDefaultAdminListTimeRange,
  parseFailedFlowQueryParams,
} from "../../contracts/admin-route-params";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminAggregateSummaryStrip } from "../../shared/ui/admin-aggregate-summary-strip";
import { AdminStatusBadge } from "../../shared/ui/admin-status-badge";
import {
  clearHiddenAdminListCursorSearchParams,
  clearExplicitAdminTimeRangeSearchParams,
  resolveExplicitAdminTimeRangeUpperBound,
  resolveAdminLiveTimeRangeRefreshSeed,
  useAdminAutoRefreshTick,
} from "../../shared/query/admin-auto-refresh";
import { AdminLiveRangeBanner } from "../../shared/ui/admin-live-range-banner";
import { FailedFlowDetailPanel } from "./failed-flow-detail-panel";
import { useFailedFlowsScreenModel } from "./use-failed-flows-screen-model";

export type FailedFlowsScreenProps = {
  dependencies: AdminDashboardDependencies;
};

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function baseControlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function createNextSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of [
    "from",
    "to",
    "feature",
    "useCase",
    "errorCode",
    "statusCode",
    "release",
    "appVersion",
    "platform",
    "deviceInstallId",
    "userIdHash",
    "petIdHash",
    "requestId",
    "correlationId",
  ]) {
    const rawValue = formData.get(key);
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

function formatScopeSummary(
  params: URLSearchParams,
  locale: "ko" | "en",
): string | undefined {
  return formatAdminDashboardSummary(locale, [
    { key: "device", value: params.get("deviceInstallId") },
    { key: "request", value: params.get("requestId") },
    { key: "correlation", value: params.get("correlationId") },
    { key: "release", value: params.get("release") },
    { key: "app", value: params.get("appVersion") },
    { key: "platform", value: params.get("platform") },
    { key: "user", value: params.get("userIdHash") },
    { key: "error", value: params.get("errorCode") },
  ]);
}

export function hasActiveFailedFlowFilters(params: URLSearchParams): boolean {
  for (const key of [
    "from",
    "to",
    "feature",
    "useCase",
    "errorCode",
    "statusCode",
    "release",
    "appVersion",
    "platform",
    "deviceInstallId",
    "userIdHash",
    "petIdHash",
    "requestId",
    "correlationId",
  ]) {
    const value = params.get(key)?.trim();
    if (value) {
      return true;
    }
  }

  return false;
}

function renderMetricCard(label: string, value: string, tone?: "default" | "warn") {
  return (
    <article
      style={{
        ...cardStyle,
        padding: "18px",
        background: tone === "warn" ? "rgba(255, 247, 237, 0.8)" : "rgba(255, 255, 255, 0.82)",
      }}
    >
      <p
        style={{
          margin: 0,
          color: "#64748b",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "10px 0 0",
          fontSize: "1.45rem",
          fontWeight: 700,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </p>
    </article>
  );
}

function isResolvedFailedFlowState(
  state: ReturnType<typeof useFailedFlowsScreenModel>,
): state is Extract<ReturnType<typeof useFailedFlowsScreenModel>, { status: "ready" | "partial" | "stale" }> {
  return state.status === "ready" || state.status === "partial" || state.status === "stale";
}

export function FailedFlowsScreen(props: FailedFlowsScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFlowId, setSelectedFlowId] = useState<string | undefined>(undefined);
  const refreshTick = useAdminAutoRefreshTick(props.dependencies.runtimeConfig);
  const effectiveSearchParams = useMemo(
    () => clearHiddenAdminListCursorSearchParams(searchParams),
    [searchParams],
  );
  const searchParamsKey = effectiveSearchParams.toString();
  const defaultTimeRangeRefreshSeed = resolveAdminLiveTimeRangeRefreshSeed(effectiveSearchParams, refreshTick);
  const explicitUpperBound = resolveExplicitAdminTimeRangeUpperBound(effectiveSearchParams);
  const defaultTimeRange = useMemo(
    () => createDefaultAdminListTimeRange(),
    [defaultTimeRangeRefreshSeed],
  );
  const query = useMemo(
    () => parseFailedFlowQueryParams(effectiveSearchParams, defaultTimeRange),
    [defaultTimeRange, effectiveSearchParams, searchParamsKey],
  );
  const hasActiveFilters = useMemo(
    () => hasActiveFailedFlowFilters(effectiveSearchParams),
    [effectiveSearchParams, searchParamsKey],
  );
  const scopeSummary = formatScopeSummary(effectiveSearchParams, locale);

  useEffect(() => {
    if (effectiveSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(withAdminDashboardLocaleSearchParams(effectiveSearchParams, locale), {
      replace: true,
    });
  }, [effectiveSearchParams, locale, searchParams, setSearchParams]);
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      query,
    }),
    [props.dependencies, locale, query],
  );
  const state = useFailedFlowsScreenModel(screenModelDependencies);
  const baselineState = useFailedFlowsScreenModel(
    useMemo(
      () => ({
        ...props.dependencies,
        locale,
        skip: !hasActiveFilters,
      }),
      [props.dependencies, locale, hasActiveFilters],
    ),
  );
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Failed Flows", ko: "오류 흐름" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Failed Flow Explorer", ko: "사용자 시나리오 실패 탐색기" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default scope: full-history latest failed flows",
      ko: "기본 범위: 전체 히스토리 최신 오류 흐름",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(locale, {
      en: "This screen opens with the latest 100 failed flows across full history. Use the filters below only when you need to narrow by request, correlation, release, platform, or device.",
      ko: "이 화면은 전체 히스토리 기준 최신 오류 흐름 100건을 먼저 엽니다. 요청, 상관관계, 릴리스, 플랫폼, 기기 기준으로 좁혀야 할 때만 아래 필터를 사용합니다.",
    }),
    authMode: pickAdminDashboardLocaleText(locale, { en: "Auth mode", ko: "인증 모드" }),
    from: pickAdminDashboardLocaleText(locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(locale, { en: "To", ko: "종료" }),
    feature: pickAdminDashboardLocaleText(locale, { en: "Feature", ko: "기능" }),
    useCase: pickAdminDashboardLocaleText(locale, { en: "Use Case", ko: "유스케이스" }),
    error: pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" }),
    httpStatus: pickAdminDashboardLocaleText(locale, { en: "HTTP status", ko: "응답 코드" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    device: pickAdminDashboardLocaleText(locale, { en: "Device", ko: "기기" }),
    platform: pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" }),
    user: pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" }),
    pet: pickAdminDashboardLocaleText(locale, { en: "Pet", ko: "펫" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    correlation: pickAdminDashboardLocaleText(locale, { en: "Correlation", ko: "상관관계" }),
    apply: pickAdminDashboardLocaleText(locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    all: pickAdminDashboardLocaleText(locale, { en: "all", ko: "전체" }),
    unknown: pickAdminDashboardLocaleText(locale, { en: "Unknown", ko: "알 수 없음" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading failed flow data...", ko: "오류 흐름 데이터를 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Failed flows denied", ko: "오류 흐름 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "Failed flows read permission is missing.", ko: "오류 흐름 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Failed flows failed", ko: "오류 흐름 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No failed flows", ko: "오류 흐름 없음" }),
    emptyBodyNoScope: pickAdminDashboardLocaleText(locale, { en: "No failed flows were found across the default full-history scope.", ko: "기본 전체 히스토리 범위에서 오류 흐름이 없습니다." }),
    emptyBodyWithScopePrefix: pickAdminDashboardLocaleText(locale, { en: "No failed flows match the current filters. filtered scope:", ko: "현재 필터로 좁힌 범위에 맞는 오류 흐름이 없습니다. 필터 범위:" }),
    eyebrow: pickAdminDashboardLocaleText(locale, { en: "Failed Flow Explorer", ko: "오류 흐름 탐색기" }),
    description: pickAdminDashboardLocaleText(locale, { en: "The first load shows the latest 100 failed flows across full history. Add request, correlation, release, platform, or device filters only when you need to narrow the investigation.", ko: "첫 진입에서는 전체 히스토리 기준 최신 오류 흐름 100건을 먼저 보여줍니다. 요청, 상관관계, 릴리스, 플랫폼, 기기 필터는 조사 범위를 좁힐 때만 추가합니다." }),
    scope: pickAdminDashboardLocaleText(locale, { en: "scope", ko: "범위" }),
    querySnapshot: pickAdminDashboardLocaleText(locale, { en: "Query snapshot", ko: "조회 스냅샷" }),
    metadata: pickAdminDashboardLocaleText(locale, { en: "Metadata", ko: "메타데이터" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    total: pickAdminDashboardLocaleText(locale, { en: "Total", ko: "총합" }),
    topError: pickAdminDashboardLocaleText(locale, { en: "Top error", ko: "상위 오류" }),
    topErrorCount: pickAdminDashboardLocaleText(locale, { en: "Top error count", ko: "상위 오류 수" }),
    totalFailedFlows: pickAdminDashboardLocaleText(locale, { en: "Total failed flows", ko: "전체 오류 흐름" }),
    distinctDevices: pickAdminDashboardLocaleText(locale, { en: "Distinct devices", ko: "고유 기기" }),
    distinctUsers: pickAdminDashboardLocaleText(locale, { en: "Distinct users", ko: "고유 사용자" }),
    incidentEvidence: pickAdminDashboardLocaleText(locale, { en: "Incident evidence", ko: "사고 근거" }),
    incidentEvidenceTitle: pickAdminDashboardLocaleText(locale, {
      en: "Read the failed-flow row before opening the incident detail",
      ko: "incident 상세를 열기 전에 failed-flow 행부터 읽습니다",
    }),
    incidentEvidenceBody: pickAdminDashboardLocaleText(locale, {
      en: "Start from the row that best matches the request, user, device, release, and runtime evidence you are investigating.",
      ko: "조사 중인 요청, 사용자, 기기, 릴리스, 런타임 근거와 가장 맞는 행에서 먼저 시작합니다.",
    }),
    created: pickAdminDashboardLocaleText(locale, { en: "Created", ko: "생성 시각" }),
    signal: pickAdminDashboardLocaleText(locale, { en: "Signal", ko: "시그널" }),
    stage: pickAdminDashboardLocaleText(locale, { en: "Stage", ko: "단계" }),
    status: pickAdminDashboardLocaleText(locale, { en: "Status", ko: "상태" }),
    job: pickAdminDashboardLocaleText(locale, { en: "Job", ko: "작업" }),
    noRowsTitle: pickAdminDashboardLocaleText(locale, { en: "No rows", ko: "행 없음" }),
    noRowsBody: pickAdminDashboardLocaleText(locale, { en: "No failed flow rows were returned.", ko: "조회된 오류 흐름 행이 없습니다." }),
    baselineTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default full-history failed flows",
      ko: "기본 전체 히스토리 오류 흐름 목록",
    }),
    baselineBody: pickAdminDashboardLocaleText(locale, {
      en: "The current filters returned no rows, so the latest 100 failed flows across full history remain visible below for the next filter choice.",
      ko: "현재 필터 결과가 비어 있어도 다음 필터 기준을 고를 수 있도록 전체 히스토리 기준 최신 오류 흐름 100건을 아래에 계속 보여줍니다.",
    }),
    baselineBodyActive: pickAdminDashboardLocaleText(locale, {
      en: "Even after filters are applied, keep the latest 100 failed flows across full history visible below so you can compare the narrowed result against the baseline and choose the next filter.",
      ko: "필터를 적용한 뒤에도 전체 히스토리 기준 최신 오류 흐름 100건을 아래에 계속 보여줘, 좁힌 결과를 baseline과 바로 비교하고 다음 필터 기준을 고를 수 있게 합니다.",
    }),
  };

  useEffect(() => {
    if (state.status === "ready" || state.status === "partial" || state.status === "stale") {
      const firstRowId = state.viewModel.rows[0]?.id;

      setSelectedFlowId((current) => {
        const matched = state.viewModel.rows.some((row) => row.id === current);
        return matched ? current : firstRowId;
      });
    } else {
      setSelectedFlowId(undefined);
    }
  }, [state]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSearchParams(createNextSearchParams(new FormData(event.currentTarget), locale));
  }

  function handleResetToLiveRange(): void {
    setSearchParams(withAdminDashboardLocaleSearchParams(clearExplicitAdminTimeRangeSearchParams(searchParams), locale));
  }

  const selectedRow =
    state.status === "ready" || state.status === "partial" || state.status === "stale"
      ? state.viewModel.rows.find((row) => row.id === selectedFlowId)
      : undefined;

  const renderBaselineContextSection = (body: string) => {
    if (!hasActiveFilters) {
      return null;
    }

    return (
      <section style={{ ...cardStyle, display: "grid", gap: "16px" }}>
        <div style={{ display: "grid", gap: "6px" }}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.baselineTitle}</p>
          <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{body}</p>
        </div>
        {baselineState.status === "loading" || baselineState.status === "idle" ? (
          <p style={{ margin: 0, color: "#475569" }}>{copy.loading}</p>
        ) : null}
        {baselineState.status === "permission_denied" ? (
          <AdminErrorState
            title={copy.deniedTitle}
            body={copy.deniedBody}
            guidance={{
              screen: "failed_flows",
              slot: "shared_error_state_guidance",
            }}
            requestId={baselineState.error.requestId}
            tone="permission_denied"
          />
        ) : null}
        {baselineState.status === "error" ? (
          <AdminErrorState
            title={copy.failedTitle}
            body={baselineState.error.message}
            guidance={{
              screen: "failed_flows",
              slot: "shared_error_state_guidance",
            }}
            requestId={baselineState.error.requestId}
            tone="error"
          />
        ) : null}
        {isResolvedFailedFlowState(baselineState) ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "14px",
              }}
            >
              <div key="baselineTotal">{renderMetricCard(copy.totalFailedFlows, baselineState.viewModel.totalCountLabel)}</div>
              <div key="baselineDevices">{renderMetricCard(copy.distinctDevices, baselineState.viewModel.distinctDeviceCountLabel)}</div>
              <div key="baselineUsers">{renderMetricCard(copy.distinctUsers, baselineState.viewModel.distinctUserCountLabel)}</div>
              <div key="baselineTopError">{renderMetricCard(copy.topError, baselineState.viewModel.topErrorCodeLabel, "warn")}</div>
            </div>
            <AdminDataTable
              columns={[
                {
                  id: "createdAt",
                  label: copy.created,
                  sortValue: (row) => row.createdAt,
                  defaultSortDirection: "desc",
                  renderCell: (row) => row.createdAtLabel,
                  width: "148px",
                },
                {
                  id: "feature",
                  label: copy.feature,
                  sortValue: (row) => row.feature,
                  renderCell: (row) => row.feature,
                },
                {
                  id: "signalKey",
                  label: copy.signal,
                  renderCell: (row) => row.signalKey,
                },
                {
                  id: "stage",
                  label: copy.stage,
                  renderCell: (row) => row.stage,
                },
                {
                  id: "errorCode",
                  label: copy.error,
                  sortValue: (row) => row.errorCode,
                  renderCell: (row) => row.errorCode,
                },
                {
                  id: "requestId",
                  label: copy.request,
                  renderCell: (row) => row.requestId ?? "-",
                },
                {
                  id: "user",
                  label: copy.user,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>{formatAdminHashedAccountCueLabel(row.userIdHash, row.accountIdentityLabel)}</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>
                        {row.accountProviderIdentityLabel}
                      </span>
                      <span style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                        {row.accountLinkedProviderCountLabel}
                      </span>
                    </div>
                  ),
                },
                {
                  id: "deviceInstallId",
                  label: copy.device,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>{row.deviceInstallIdLabel}</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.runtimeSummaryLabel}</span>
                    </div>
                  ),
                },
                {
                  id: "release",
                  label: copy.release,
                  renderCell: (row) => row.release ?? "-",
                },
              ]}
              getRowId={(row) => row.id}
              rows={baselineState.viewModel.rows}
            />
          </>
        ) : null}
      </section>
    );
  };

  const renderFilterForm = () => (
    <form onSubmit={handleFilterSubmit}>
      <AdminFilterBar
        items={[
          {
            id: "from",
            label: copy.from,
            control: (
              <input defaultValue={searchParams.get("from") ?? ""} id="from" name="from" style={baseControlStyle()} />
            ),
          },
          {
            id: "to",
            label: copy.to,
            control: (
              <input defaultValue={searchParams.get("to") ?? ""} id="to" name="to" style={baseControlStyle()} />
            ),
          },
          {
            id: "feature",
            label: copy.feature,
            control: (
              <input
                defaultValue={searchParams.get("feature") ?? ""}
                id="feature"
                name="feature"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "useCase",
            label: copy.useCase,
            control: (
              <input
                defaultValue={searchParams.get("useCase") ?? ""}
                id="useCase"
                name="useCase"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "errorCode",
            label: copy.error,
            control: (
              <input
                defaultValue={searchParams.get("errorCode") ?? ""}
                id="errorCode"
                name="errorCode"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "statusCode",
            label: copy.httpStatus,
            control: (
              <input
                defaultValue={searchParams.get("statusCode") ?? ""}
                id="statusCode"
                name="statusCode"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "release",
            label: copy.release,
            control: (
              <input
                defaultValue={searchParams.get("release") ?? ""}
                id="release"
                name="release"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "appVersion",
            label: copy.app,
            control: (
              <input
                defaultValue={searchParams.get("appVersion") ?? ""}
                id="appVersion"
                name="appVersion"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "deviceInstallId",
            label: copy.device,
            control: (
              <input
                defaultValue={searchParams.get("deviceInstallId") ?? ""}
                id="deviceInstallId"
                name="deviceInstallId"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "platform",
            label: copy.platform,
            control: (
              <select
                defaultValue={searchParams.get("platform") ?? ""}
                id="platform"
                name="platform"
                style={baseControlStyle()}
              >
                <option value="">{copy.all}</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
                <option value="web">Web</option>
                <option value="api">API</option>
                <option value="unknown">{copy.unknown}</option>
              </select>
            ),
          },
          {
            id: "userIdHash",
            label: copy.user,
            control: (
              <input
                defaultValue={searchParams.get("userIdHash") ?? ""}
                id="userIdHash"
                name="userIdHash"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "petIdHash",
            label: copy.pet,
            control: (
              <input
                defaultValue={searchParams.get("petIdHash") ?? ""}
                id="petIdHash"
                name="petIdHash"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "requestId",
            label: copy.request,
            control: (
              <input
                defaultValue={searchParams.get("requestId") ?? ""}
                id="requestId"
                name="requestId"
                style={baseControlStyle()}
              />
            ),
          },
          {
            id: "correlationId",
            label: copy.correlation,
            control: (
              <input
                defaultValue={searchParams.get("correlationId") ?? ""}
                id="correlationId"
                name="correlationId"
                style={baseControlStyle()}
              />
            ),
          },
        ]}
        actions={
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              style={{
                minHeight: "40px",
                padding: "0 14px",
                borderRadius: "999px",
                border: "none",
                background: "#0f766e",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
              type="submit"
            >
              {copy.apply}
            </button>
            <AdminLocalizedLink
              to="/admin/failed-flows"
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "40px",
                padding: "0 14px",
                borderRadius: "999px",
                border: "1px solid rgba(148, 163, 184, 0.45)",
                color: "#18212b",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              {copy.reset}
            </AdminLocalizedLink>
          </div>
        }
      />
    </form>
  );

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
            <div style={{ marginTop: "14px" }}>
              <AdminOperatorExplanationBlock screen="failed_flows" slot="failed_flows_top_summary" />
            </div>
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {copy.authMode}: {formatAdminAuthModeLabel(props.dependencies.runtimeConfig.authMode, locale)}
          </p>
        </div>
      </header>

      {explicitUpperBound ? (
        <AdminLiveRangeBanner
          explicitUpperBound={explicitUpperBound}
          locale={locale}
          onResetToLive={handleResetToLiveRange}
        />
      ) : null}

      <section style={{ ...cardStyle, display: "grid", gap: "8px" }}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.defaultScopeTitle}</p>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
      </section>

      {state.status === "loading" || state.status === "idle" ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "failed_flows",
            slot: "shared_error_state_guidance",
          }}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "failed_flows",
            slot: "shared_error_state_guidance",
          }}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {state.status === "empty" ? (
        <>
          <AdminEmptyState
            title={copy.emptyTitle}
            body={
              scopeSummary
                ? `${copy.emptyBodyWithScopePrefix} ${scopeSummary}`
                : copy.emptyBodyNoScope
            }
            guidance={{
              screen: "failed_flows",
              slot: "shared_empty_state_guidance",
            }}
          />
          {renderBaselineContextSection(copy.baselineBody)}
        </>
      ) : null}

      {state.status === "ready" || state.status === "partial" || state.status === "stale" ? (
        <>
          <section
            style={{
              ...cardStyle,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.85fr)",
              gap: "18px",
            }}
          >
            <div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                {copy.eyebrow}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.35rem", fontWeight: 700 }}>
                {state.viewModel.selectedScopeLabel}
              </p>
              <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>
                {copy.description}
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
                <AdminStatusBadge value={state.status} />
                {scopeSummary ? (
                  <span style={{ color: "#64748b", fontSize: "0.88rem" }}>{copy.scope}: {scopeSummary}</span>
                ) : null}
              </div>
            </div>

            <AdminDetailPanel
              title={copy.querySnapshot}
              sections={[
                {
                  title: copy.metadata,
                  items: [
                    { key: "generatedAt", label: copy.generated, value: state.viewModel.generatedAtLabel },
                    { key: "requestId", label: copy.request, value: state.viewModel.requestId },
                    { key: "totalCount", label: copy.total, value: state.viewModel.totalCountLabel },
                    { key: "topErrorCode", label: copy.topError, value: state.viewModel.topErrorCodeLabel },
                    { key: "topErrorCount", label: copy.topErrorCount, value: state.viewModel.topErrorCountLabel },
                  ],
                },
              ]}
            />
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "14px",
            }}
          >
            <div key="total">{renderMetricCard(copy.totalFailedFlows, state.viewModel.totalCountLabel)}</div>
            <div key="devices">{renderMetricCard(copy.distinctDevices, state.viewModel.distinctDeviceCountLabel)}</div>
            <div key="users">{renderMetricCard(copy.distinctUsers, state.viewModel.distinctUserCountLabel)}</div>
            <div key="topError">{renderMetricCard(copy.topError, state.viewModel.topErrorCodeLabel, "warn")}</div>
          </div>

          {state.viewModel.observabilityAggregate ? (
            <AdminAggregateSummaryStrip
              ariaLabel={state.viewModel.observabilityAggregate.title}
              items={state.viewModel.observabilityAggregate.items}
              sourceGapLabel={state.viewModel.observabilityAggregate.sourceGapLabel}
              title={state.viewModel.observabilityAggregate.title}
            />
          ) : null}

          {state.viewModel.rowHotspotSnapshot ? (
            <AdminAggregateSummaryStrip
              ariaLabel={state.viewModel.rowHotspotSnapshot.title}
              items={state.viewModel.rowHotspotSnapshot.items}
              sourceGapLabel={state.viewModel.rowHotspotSnapshot.sourceGapLabel}
              title={state.viewModel.rowHotspotSnapshot.title}
            />
          ) : null}

          <section style={{ display: "grid", gap: "8px" }}>
            <div style={{ display: "grid", gap: "6px" }}>
              <p
                style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {copy.incidentEvidence}
              </p>
              <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#18212b" }}>
                {copy.incidentEvidenceTitle}
              </p>
              <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                {copy.incidentEvidenceBody}
              </p>
            </div>
            <AdminOperatorExplanationBlock
              screen="failed_flows"
              slot="failed_flows_table_reading_order"
            />
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.45fr) minmax(300px, 0.85fr)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <AdminDataTable
              columns={[
                {
                  id: "createdAt",
                  label: copy.created,
                  sortValue: (row) => row.createdAt,
                  defaultSortDirection: "desc",
                  renderCell: (row) => row.createdAtLabel,
                  width: "148px",
                },
                {
                  id: "feature",
                  label: copy.feature,
                  sortValue: (row) => row.feature,
                  renderCell: (row) => row.feature,
                },
                {
                  id: "signalKey",
                  label: copy.signal,
                  renderCell: (row) => row.signalKey,
                },
                {
                  id: "stage",
                  label: copy.stage,
                  renderCell: (row) => row.stage,
                },
                {
                  id: "useCase",
                  label: copy.useCase,
                  renderCell: (row) => row.useCase,
                },
                {
                  id: "status",
                  label: copy.status,
                  renderCell: (row) => (
                    <AdminStatusBadge value={row.status} label={row.statusLabel} />
                  ),
                },
                {
                  id: "errorCode",
                  label: copy.error,
                  sortValue: (row) => row.errorCode,
                  renderCell: (row) => row.errorCode,
                },
                {
                  id: "statusCode",
                  label: copy.httpStatus,
                  align: "right",
                  renderCell: (row) => row.statusCodeLabel,
                },
                {
                  id: "requestId",
                  label: copy.request,
                  renderCell: (row) => row.requestId ?? "-",
                },
                {
                  id: "correlationId",
                  label: copy.correlation,
                  renderCell: (row) => row.correlationId ?? "-",
                },
                {
                  id: "user",
                  label: copy.user,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>{formatAdminHashedAccountCueLabel(row.userIdHash, row.accountIdentityLabel)}</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>
                        {row.accountProviderIdentityLabel}
                      </span>
                    </div>
                  ),
                },
                {
                  id: "deviceInstallId",
                  label: copy.device,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <span>{row.deviceInstallIdLabel}</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.runtimeSummaryLabel}</span>
                    </div>
                  ),
                },
                {
                  id: "jobId",
                  label: copy.job,
                  renderCell: (row) => row.jobId ?? row.deletionJobId ?? "-",
                },
                {
                  id: "release",
                  label: copy.release,
                  renderCell: (row) => row.release ?? "-",
                },
                {
                  id: "appVersion",
                  label: copy.app,
                  renderCell: (row) => row.appVersion ?? "-",
                },
                {
                  id: "platform",
                  label: copy.platform,
                  renderCell: (row) => row.platformLabel,
                },
              ]}
              emptyState={<AdminEmptyState title={copy.noRowsTitle} body={copy.noRowsBody} />}
              getRowId={(row) => row.id}
              onRowClick={(row) => setSelectedFlowId(row.id)}
              rows={state.viewModel.rows}
            />

            <FailedFlowDetailPanel
              detail={selectedRow?.detail}
              sourceFreshness={state.viewModel.sourceFreshness}
            />
          </div>

          {renderBaselineContextSection(copy.baselineBodyActive)}
        </>
      ) : null}

      {renderFilterForm()}
    </section>
  );
}
